import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { chatServerInstanceWithToken } from '@/helpers/chat_server_instance';
import { MessageInput } from './MessageInput';
import { FaEye } from 'react-icons/fa6';
import Swal from 'sweetalert2';
export const Messages = ({ chat, socket }) => {
    const [messages, setMessages] = useState([]);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const userId = JSON.parse(localStorage.getItem('data'))?.id;
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);

    useEffect(() => {
        // Fetch chat messages when component mounts
        chatServerInstanceWithToken.get(`/getMessages/${chat.chat_id}`)
            .then(response => setMessages(response.data || []))
            .catch(error => console.error("Error fetching messages:", error));

        // Listen for new messages
        socket.on('newMessage', (data) => {
            // Update messages list with the new message
            setMessages((prevMessages) => [
                ...prevMessages,
                {
                    sender: {
                        id: data.sender_id,
                        name: data?.newMessage?.sender_name,
                    },
                    message: data.message,
                    chat_id: data.chat_id
                },
            ]);
        });

        return () => {
            socket.off('newMessage');
        };
    }, [chat, socket]);

    // Function to trigger admin involvement (e.g., opening a modal)
    const involveAdmin = async () => {
        const chatId = chat?.chat_id;
        const adminId = 1; // Replace with actual admin ID or get it dynamically

        try {
            // Show loading indicator
            Swal.fire({
                title: 'Requesting Admin Assistance',
                text: 'Please wait while we notify an admin...',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await chatServerInstanceWithToken.put('/addAdminToChat', {
                chat_id: chatId,
                admin_id: adminId,
            });

            // Close loading indicator
            Swal.close();

            // Show success message
            await Swal.fire({
                icon: 'success',
                title: 'Admin Notified!',
                text: 'An admin has been assigned to assist with this conversation.',
                confirmButtonText: 'OK',
                timer: 3000,
                timerProgressBar: true,
            });

            // Optional: Handle any UI updates after success
            console.log('Admin assignment response:', response.data);

        } catch (error) {
            // Close loading indicator if it's still open
            Swal.close();

            // Show error message
            await Swal.fire({
                icon: 'error',
                title: 'Request Failed',
                text: error.response?.data?.message || 'Failed to notify admin. Please try again later.',
                confirmButtonText: 'OK'
            });

            console.error('Admin involvement error:', error);
        }
    };

    // Detect if user is scrolling
    useEffect(() => {
        const handleScroll = () => {
            if (!chatContainerRef.current) return;

            const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;

            // User is scrolling if they are not near the bottom
            setIsUserScrolling(scrollTop + clientHeight < scrollHeight - 50);
        };

        const chatContainer = chatContainerRef.current;
        if (chatContainer) {
            chatContainer.addEventListener("scroll", handleScroll);
        }

        return () => {
            if (chatContainer) {
                chatContainer.removeEventListener("scroll", handleScroll);
            }
        };
    }, []);

    // Auto-scroll only if the user is not actively scrolling
    useEffect(() => {
        if (!isUserScrolling) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }

        try {
            chatServerInstanceWithToken.put(`/markMessagesAsRead/${chat.chat_id}`, { user_id: userId });
        } catch (error) {
            console.error("Error marking messages as read:", error);
        }
    }, [chat.chat_id, messages, userId, isUserScrolling]);


    const markAsUnread = async (messageId) => {
        try {
            const response = await chatServerInstanceWithToken.put(`/messages/${messageId}/unread`);
            if (response.data.success) {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: "Message marked as unread!",
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                Swal.fire({
                    icon: "warning",
                    title: "Not Found",
                    text: "Message not found!",
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error("Error marking message as unread:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to mark message as unread.",
            });
        }
    };


    return (
        <div className="flex flex-col h-full bg-white">
            <div>
                {/* Floating Button */}
                <button
                    onClick={involveAdmin}
                    className="absolute top-4 right-4 p-3 bg-[#8ec639] text-white rounded-full shadow-lg hover:bg-green-600 focus:outline-none z-50"
                >
                    <i className="fa fa-user-shield"></i> {/* Icon for admin */}
                </button>
            </div>

            {/* Messages List */}
            <div ref={chatContainerRef} className="flex-1 p-4 pb-12 overflow-y-auto">
                {messages?.map((msg, index) => {
                    return (
                        <div className={`w-full flex ${msg?.sender?.id === userId ? 'justify-end' : ''}`} key={index}>

                            <div
                                className={`mb-3 max-w-[80%] p-3 rounded-2xl ${msg?.sender?.id === userId
                                    ? 'bg-[#8ec639] text-white self-end shadow-lg'
                                    : 'bg-white border border-[#ddd] text-gray-700 self-start shadow-sm'
                                    } transition-all duration-300 ease-in-out transform hover:scale-105`}
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <span
                                        className={`text-xs font-semibold ${msg?.sender?.id === userId
                                            ? 'text-white'
                                            : 'text-gray-700'
                                            }`}
                                    >
                                        {msg?.sender?.id === 1 ? `${msg?.sender?.name} (Admin)` : msg?.sender?.name}
                                    </span>

                                    {msg?.sender?.id !== userId && (
                                        <FaEye
                                            className={`cursor-pointer text-gray-500 hover:text-blue-500 mr-2 ${msg?.status === 'unread' ? 'text-[#8ec639]' : ''}`}
                                            onClick={() => markAsUnread(msg?.message_id)}
                                        />
                                    )}
                                </div>

                                <hr className='border-gray-300' />
                                <p className={`!m-0 !p-0 font-medium break-words ${msg?.sender?.id === userId ? 'text-white' : 'text-gray-700'}`}>
                                    {msg?.message}
                                </p>

                                <div className="flex items-center justify-between mt-2">
                                    <span className={`text-xs ${msg?.sender?.id === userId ? 'text-white' : 'text-gray-500'}`}>
                                        {moment(msg?.created_at).format('DD MMM YYYY h:mm A')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )
                })}

                {/* Invisible div to keep scrolling to the last message */}
                <div ref={messagesEndRef} />
            </div>

            {/* Sticky Message Input */}
            <div className="sticky bottom-0 w-full p-3 bg-white border-t border-gray-300">
                <MessageInput chatId={chat.chat_id} socket={socket} />
            </div>
        </div>

    );
};
